import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import api from '../../utils/api';

interface HistoricalNotesControlProps {
  clientId: string;
  allowHistoricalNotes: boolean;
  historicalNotesDeadline?: string;
  historicalNotesCompletedFor?: Array<{
    counsellorId: string;
    completedAt: string;
    sessionCount: number;
  }>;
  onUpdate: () => void;
}

interface CounsellorCompletion {
  counsellorId: string;
  counsellorName: string;
  completedAt: string;
  sessionCount: number;
}

const HistoricalNotesControl: React.FC<HistoricalNotesControlProps> = ({
  clientId,
  allowHistoricalNotes,
  historicalNotesDeadline,
  historicalNotesCompletedFor = [],
  onUpdate
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [completionDetails, setCompletionDetails] = useState<CounsellorCompletion[]>([]);

  const handleToggle = async () => {
    if (!isEffectivelyEnabled) {
      // Enabling (or re-arming after the deadline lapsed) - show confirmation.
      // /enable always sets a fresh 2-week deadline.
      setConfirmDialogOpen(true);
    } else {
      // Disabling
      await disableHistoricalNotes();
    }
  };

  const enableHistoricalNotes = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const response = await api.patch(`/api/historical-notes/admin/clients/${clientId}/enable`);

      setSuccess(`Historical notes enabled. ${response.data.notifiedCounsellors} counsellor(s) notified.`);
      setConfirmDialogOpen(false);
      
      // Refresh parent component
      setTimeout(() => {
        onUpdate();
        setSuccess(null);
      }, 2000);

    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to enable historical notes');
    } finally {
      setLoading(false);
    }
  };

  const disableHistoricalNotes = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await api.patch(`/api/historical-notes/admin/clients/${clientId}/disable`);

      setSuccess('Historical notes disabled successfully');
      
      // Refresh parent component
      setTimeout(() => {
        onUpdate();
        setSuccess(null);
      }, 2000);

    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to disable historical notes');
    } finally {
      setLoading(false);
    }
  };

  const loadCompletionDetails = async () => {
    try {
      // Fetch counsellor names for completed entries
      const details = await Promise.all(
        historicalNotesCompletedFor.map(async (completion) => {
          try {
            const response = await api.get(`/api/users/${completion.counsellorId}`);
            return {
              ...completion,
              counsellorName: `${response.data.firstName} ${response.data.lastName}`
            };
          } catch {
            return {
              ...completion,
              counsellorName: 'Unknown Counsellor'
            };
          }
        })
      );
      setCompletionDetails(details);
    } catch (err) {
      console.error('Error loading completion details:', err);
    }
  };

  React.useEffect(() => {
    if (historicalNotesCompletedFor.length > 0) {
      loadCompletionDetails();
    }
  }, [historicalNotesCompletedFor]);

  const isDeadlinePassed = historicalNotesDeadline
    ? new Date(historicalNotesDeadline) < new Date()
    : false;

  // The stored flag stays true after the deadline passes - the deadline is
  // the real gate (server-side rejects submissions after it). Show the
  // switch as the *effective* state so a lapsed window doesn't look active.
  const isEffectivelyEnabled = allowHistoricalNotes && !isDeadlinePassed;

  return (
    <Box
      className="glass-card"
      sx={{
        p: 3,
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        mt: 3,
        mb: 3
      }}
    >
      <Typography variant="h6" className="theme-text-primary" sx={{ fontWeight: 600, mb: 2 }}>
        Historical Session Notes
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={isEffectivelyEnabled}
              onChange={handleToggle}
              disabled={loading}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#4caf50'
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#4caf50'
                }
              }}
            />
          }
          label={
            <Typography className="theme-text-primary">
              Allow Historical Notes Entry
            </Typography>
          }
        />
        {loading && <CircularProgress size={20} sx={{ ml: 2 }} />}
      </Box>

      {allowHistoricalNotes && historicalNotesDeadline && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={`Deadline: ${new Date(historicalNotesDeadline).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}${isDeadlinePassed ? ' (passed)' : ''}`}
            color={isDeadlinePassed ? 'error' : 'warning'}
            size="small"
          />
        </Box>
      )}

      {completionDetails.length > 0 && (
        <Box>
          <Typography variant="body2" className="theme-text-secondary" sx={{ fontWeight: 600, mb: 1 }}>
            Completed By:
          </Typography>
          {completionDetails.map((completion, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
                p: 1.5,
                borderRadius: '8px',
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                border: '1px solid rgba(76, 175, 80, 0.3)'
              }}
            >
              <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" className="theme-text-primary" sx={{ fontWeight: 600 }}>
                  {completion.counsellorName}
                </Typography>
                <Typography variant="caption" className="theme-text-secondary">
                  {completion.sessionCount} session{completion.sessionCount !== 1 ? 's' : ''} • Completed{' '}
                  {new Date(completion.completedAt).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {isEffectivelyEnabled && completionDetails.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            Counsellors have been notified and can now add historical session notes for this client.
          </Typography>
        </Alert>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => !loading && setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h6" className="theme-text-primary" sx={{ fontWeight: 600 }}>
            Enable Historical Notes?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography className="theme-text-primary" sx={{ mb: 2 }}>
            This will:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <Typography component="li" className="theme-text-primary" sx={{ mb: 1 }}>
              Allow current and past counsellors to add historical session notes
            </Typography>
            <Typography component="li" className="theme-text-primary" sx={{ mb: 1 }}>
              Send email notifications to all relevant counsellors
            </Typography>
            <Typography component="li" className="theme-text-primary" sx={{ mb: 1 }}>
              Set a deadline of 2 weeks from now
            </Typography>
            <Typography component="li" className="theme-text-primary">
              This is a one-time opportunity per counsellor-client relationship
            </Typography>
          </Box>
          <Alert severity="warning">
            <Typography variant="body2">
              Once enabled, counsellors will have 2 weeks to complete their historical notes. This action cannot be undone.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            disabled={loading}
            sx={{
              textTransform: 'none',
              color: 'rgba(255, 255, 255, 0.6)',
              '&:hover': {
                color: 'rgba(255, 255, 255, 0.8)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={enableHistoricalNotes}
            disabled={loading}
            variant="contained"
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)'
              }
            }}
          >
            {loading ? 'Enabling...' : 'Enable Historical Notes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HistoricalNotesControl;
